<!--
  LwField: the product's first field-level validation surface.

  Fixes web paper cut 10 (75 of 88 labels have no `for`, audit_web.md 3.5) by
  always wiring label/control association, either from the `id` prop or from a
  generated id, and fixes web paper cut 9 (Enter does not submit on 22 of 25
  forms) by emitting `submit` on Enter without swallowing the key. It also
  gives Create Load and friends somewhere to put a 422 response: `error`
  renders next to the control, in a `role="alert"` element referenced by
  `aria-describedby`, so the failure is both visible and announced
  (refresh_plan.md 3.1.6, audit_web.md 3.5).

  Replaces `.form-control` outright (refresh_plan.md 2.3): input appearance is
  owned by tokens here, not by Bootstrap's `_forms` partial and its blue focus
  glow. `type="select"` (added for the phase 2 dispatch board migration,
  refresh_plan.md 2.3/3.1.6) extends that same replacement to `<select
  class="form-control">`, which is the other half of `_forms` a page could
  otherwise reach for once the input half is gone.

  `type="textarea"` (added for phase 2 surface 3, the Create Load / Edit Load
  forms) is the third and last `.form-control` variant those forms use: Notes
  today is a bare `<textarea class="form-control" rows="6">` next to a bare
  `<label><strong>Notes</strong></label>` with no `for` at all, the same
  paper-cut-10 gap this component exists to close everywhere else. It renders
  as its own branch rather than joining `TEXT_LIKE_TYPES`, for the same
  reason `type="select"` did: that list is also how the input branch below
  decides `<input :type>`, and `textarea` is not an input type. `datetime-local`
  joins `TEXT_LIKE_TYPES` itself, unremarkably, since it IS an input type: the
  load forms' stop times move off `vue2-datepicker`, whose stylesheet
  (`vue2-datepicker/lib/datepicker.css`) is imported nowhere in this repo, so
  the widget renders entirely unstyled today. The alternative to reaching for
  the platform control was importing a third party's raw hex, which the
  token-purity gate exists to refuse.

  Three additions for the pages that save one field per change (spec 49
  section 6: Preferences, and Daily digest which follows its discipline):

  - `type="checkbox"` renders an LwSwitch, named by this field's label and
    described by its description, so a boolean setting gets a tokened
    control instead of the browser's own checkbox through the slot.
  - `update-on="change"` makes the text, number and textarea branches emit
    `update:modelValue` on the native `change` event (when the field is
    left) instead of on every keystroke. A page that PATCHes per field must
    not PATCH per character. The select and switch branches are unaffected:
    they only ever change discretely.
  - `status` ('saving' | 'saved' | 'error' | null) draws a small live
    indicator beside the label, so one-field-per-request saving reads as
    the deliberate pattern it is rather than a form with no Save button.
    The words are the signal; the colour and glyph only accompany them.
-->
<template>
  <div class="lw-c-field">
    <div class="lw-c-field__head">
      <label :for="controlId" class="lw-c-field__label">
        {{ label }}
        <!--
          A visible marker plus an accessible name, never a bare asterisk with no
          text alternative. aria-label is a global attribute; it is honoured on a
          plain span with no role, which is all a plate-face glyph needs to be.
        -->
        <span v-if="required" class="lw-c-field__required" aria-label="required">*</span>
      </label>
      <!--
        A sibling of the label, not a child: text inside <label> joins the
        control's accessible name, and "Rounding Saving…" is not the name of
        anything. role="status" is a polite live region, so the save is
        announced after whatever the person is doing, never over it.
      -->
      <span
        v-if="status"
        class="lw-c-field__status"
        :class="`lw-c-field__status--${status}`"
        role="status"
        data-test="field-status"
      >
        <span v-if="status === 'saving'" class="lw-c-field__spinner" aria-hidden="true" />
        <i v-else-if="status === 'saved'" class="fa fa-check" aria-hidden="true" />
        <i v-else class="fa fa-exclamation-circle" aria-hidden="true" />
        {{ statusText }}
      </span>
    </div>

    <slot
      :id="controlId"
      :describedby="describedBy"
      :invalid="hasError"
      :disabled="disabled"
      :required="required"
    >
      <LwSwitch
        v-if="isCheckbox"
        :id="controlId"
        ref="control"
        :model-value="modelValue === true"
        :describedby="describedBy"
        :disabled="disabled"
        @update:model-value="$emit('update:modelValue', $event)"
      />
      <select
        v-else-if="isSelect"
        :id="controlId"
        ref="control"
        class="lw-c-field__control lw-c-field__control--select"
        :class="controlClasses"
        :required="required"
        :disabled="disabled"
        :aria-describedby="describedBy"
        :aria-invalid="hasError ? 'true' : null"
        @change="handleSelectChange"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
        @keydown.enter="handleEnter"
      >
        <!--
          A real choice would round-trip through the same
          String(option.value) === String(modelValue) comparison as every other
          option below and, once picked, be indistinguishable from a value the
          driver actually chose. `disabled` keeps it selectable only while
          nothing else is, so it functions as an unset marker instead of a
          25th driver named "Choose a driver".
        -->
        <option v-if="placeholder" value="" disabled :selected="isPlaceholderSelected">
          {{ placeholder }}
        </option>
        <option
          v-for="option in options || []"
          :key="option.value"
          :value="option.value"
          :selected="String(option.value) === String(modelValue)"
        >
          {{ option.label }}
        </option>
      </select>
      <!--
        No @keydown.enter handler on a textarea, unlike the select and input
        branches above. Enter in a multi-line control inserts a newline; that
        is the whole point of the control existing instead of a single-line
        input. The other two branches emit `submit` on Enter to fix web paper
        cut 9 (Enter does not submit on 22 of 25 forms, audit_web.md 3.5), but
        a textarea is the one control on which swallowing that key for
        submission would be wrong, so this branch leaves the native default
        action alone and emits nothing extra.
      -->
      <textarea
        v-else-if="isTextarea"
        :id="controlId"
        ref="control"
        class="lw-c-field__control lw-c-field__control--textarea"
        :class="controlClasses"
        :rows="rows"
        :maxlength="maxlength"
        :value="modelValue"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :aria-describedby="describedBy"
        :aria-invalid="hasError ? 'true' : null"
        @input="handleInput"
        @change="handleChange"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />
      <input
        v-else
        :id="controlId"
        ref="control"
        class="lw-c-field__control"
        :class="controlClasses"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :min="min"
        :max="max"
        :step="step"
        :autocomplete="autocomplete"
        :inputmode="inputmode"
        :required="required"
        :disabled="disabled"
        :aria-describedby="describedBy"
        :aria-invalid="hasError ? 'true' : null"
        @input="handleInput"
        @change="handleChange"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
        @keydown.enter="handleEnter"
      />
    </slot>

    <p
      v-if="description"
      :id="descriptionId"
      class="lw-c-field__description"
      :class="attention ? `lw-c-field__description--${attention}` : null"
    >
      {{ description }}
    </p>

    <!--
      role="alert" so the message is in the accessibility tree the instant the
      error appears (an assertive live region announces on insertion, it does
      not need to already be mounted and waiting).
    -->
    <p v-if="error" :id="errorId" class="lw-c-field__error" role="alert">
      <slot name="error">
        {{ error }}
      </slot>
    </p>
  </div>
</template>

<script>
import LwSwitch from './LwSwitch.vue';

// Module-level counter so every unmounted-id instance gets a distinct,
// stable id: `lw-field-1`, `lw-field-2`, ... This is what fixes the 75 of 88
// labels with no `for` (audit_web.md 3.5) even when a caller never passes an
// id at all.
let idCounter = 0;
function nextGeneratedId() {
  idCounter += 1;
  return `lw-field-${idCounter}`;
}

// 'datetime-local' belongs here, not in a branch of its own: it IS an input
// type, so it renders through the same `<input :type>` the rest of this list
// does. See the top-of-file comment for why the load forms need it (moving
// stop times off an unstyled vue2-datepicker without importing raw hex).
const TEXT_LIKE_TYPES = [
  'text',
  'email',
  'tel',
  'number',
  'password',
  'search',
  'url',
  'date',
  'datetime-local',
];
// Kept out of TEXT_LIKE_TYPES rather than appended to it: that list is also
// how the template used to decide `<input :type>`, and 'select' is not an
// input type at all, it picks the other branch entirely.
const SELECT_TYPE = 'select';
// Same reasoning as SELECT_TYPE: 'textarea' is not an input type, and this
// list is also how the template picks `<input :type>`, so it stays a
// separate branch rather than a TEXT_LIKE_TYPES entry.
const TEXTAREA_TYPE = 'textarea';
// And a fourth branch, for the same reason again: a checkbox here is not an
// `<input type="checkbox">` but an LwSwitch, a button with role="switch".
const CHECKBOX_TYPE = 'checkbox';

const UPDATE_EVENTS = ['input', 'change'];
const STATUSES = ['saving', 'saved', 'error'];
const STATUS_TEXT = { saving: 'Saving…', saved: 'Saved', error: 'Not saved' };

export default {
  name: 'LwField',
  components: { LwSwitch },
  props: {
    label: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      default: null,
    },
    // Boolean for type="checkbox"; a switch that is handed anything other
    // than `true` is off, so an unset setting never renders as on.
    modelValue: {
      type: [String, Number, Boolean],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
      validator: value =>
        TEXT_LIKE_TYPES.includes(value) ||
        value === SELECT_TYPE ||
        value === TEXTAREA_TYPE ||
        value === CHECKBOX_TYPE,
    },
    // Which native event the text, number and textarea branches emit
    // `update:modelValue` on. 'input' (every keystroke) is right for a form
    // with a Save button; 'change' (when the field is left) is right for a
    // page that saves each field as it is edited, which must not send a
    // request per character. The select and switch only change discretely,
    // so the prop does not apply to them.
    updateOn: {
      type: String,
      default: 'input',
      validator: value => UPDATE_EVENTS.includes(value),
    },
    // The per-field save indicator beside the label: 'saving' (a spinner
    // and the word), 'saved' (a check and the word), 'error' (the word; the
    // page's own notice carries the sentence), or null for nothing.
    status: {
      type: String,
      default: null,
      validator: value => value === null || STATUSES.includes(value),
    },
    // The platform's own bounds for type="number", forwarded as attributes.
    // null renders no attribute, which is what a value with no bound should
    // get rather than a 0 nobody asked for. They are a convenience, not the
    // check: a number typed rather than spun reaches the server regardless.
    min: {
      type: [Number, String],
      default: null,
    },
    max: {
      type: [Number, String],
      default: null,
    },
    step: {
      type: [Number, String],
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    error: {
      type: String,
      default: null,
    },
    // A ring that is not a validation error: the value is valid but something
    // about it needs a look (Create load rings a stop time the chosen driver
    // cannot make). 'warning' or 'danger'; the description carries the words,
    // so the colour is never the only signal. `error` wins when both are set.
    attention: {
      type: String,
      default: null,
      validator: value => value === null || value === 'warning' || value === 'danger',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: null,
    },
    autocomplete: {
      type: String,
      default: null,
    },
    inputmode: {
      type: String,
      default: null,
    },
    // { value, label } pairs for type="select". `value` is left untyped
    // deliberately: the dispatch board's options carry numeric database ids
    // (driver_id, truck_id, trailer_id) that get PATCHed straight back to the
    // API, and a String prop-type here would be exactly the kind of silent
    // coercion this component exists to prevent.
    options: {
      type: Array,
      default: null,
    },
    // Rows for type="textarea" only; ignored by the other two branches. 4 is
    // a sensible default for a short free-text field — Create Load and Edit
    // Load pass rows="6" explicitly for Notes, the same way the old
    // hand-rolled `<textarea class="form-control" rows="6">` did.
    rows: {
      type: Number,
      default: 4,
    },
    // The platform's own length cap, forwarded to the text-like input and
    // textarea branches (a select has nothing to cap). Added for spec 28's
    // invoice fields, whose limits are QuickBooks' (1000 for the customer
    // memo, 4000 for the private note): the API refuses an over-long value
    // with a 400, and a control that stops accepting input at the limit is
    // the cheaper of the two ways to learn it. null renders no attribute.
    maxlength: {
      type: Number,
      default: null,
    },
  },
  emits: ['update:modelValue', 'blur', 'focus', 'submit'],
  data() {
    return {
      // Generated once, in data(), which runs a single time when the instance
      // is created, not on every render. `this.id || nextGeneratedId()` also
      // short-circuits: an instance that was given an id never touches the
      // counter, so ids stay stable across props changing elsewhere on the
      // component.
      generatedId: this.id || nextGeneratedId(),
    };
  },
  computed: {
    controlId() {
      return this.id || this.generatedId;
    },
    hasError() {
      return !!this.error;
    },
    controlClasses() {
      return {
        'lw-c-field__control--error': this.hasError,
        [`lw-c-field__control--attention-${this.attention}`]: !this.hasError && !!this.attention,
      };
    },
    descriptionId() {
      return this.description ? `${this.controlId}-description` : null;
    },
    errorId() {
      return this.error ? `${this.controlId}-error` : null;
    },
    // aria-describedby references whichever of description/error exist, both
    // if both do. Contract: the referenced ids must actually exist in the
    // rendered DOM, which they do because descriptionId/errorId are only
    // non-null exactly when their element is v-if'd in.
    describedBy() {
      const ids = [this.descriptionId, this.errorId].filter(Boolean);
      return ids.length ? ids.join(' ') : null;
    },
    isSelect() {
      return this.type === SELECT_TYPE;
    },
    isTextarea() {
      return this.type === TEXTAREA_TYPE;
    },
    isCheckbox() {
      return this.type === CHECKBOX_TYPE;
    },
    statusText() {
      return STATUS_TEXT[this.status] || null;
    },
    // A select has no "empty string in a text box" state; unset is the
    // placeholder option being the one that is selected. Treated as unset for
    // both null (no value supplied yet) and '' (an explicit clear), since a
    // caller resetting a v-model to '' expects the placeholder back, not a
    // real option coincidentally valued ''.
    isPlaceholderSelected() {
      return this.modelValue === null || this.modelValue === '';
    },
  },
  methods: {
    handleInput(event) {
      if (this.updateOn !== 'input') return;
      this.$emit('update:modelValue', event.target.value);
    },
    // The native `change` fires when the control is left after editing (and,
    // for a number input, when its spinner is used). Under update-on="input"
    // the value was already emitted keystroke by keystroke, so this emits
    // nothing extra rather than a duplicate the page would save twice.
    handleChange(event) {
      if (this.updateOn !== 'change') return;
      this.$emit('update:modelValue', event.target.value);
    },
    // A DOM select's `event.target.value` is always a string (HTMLOptionElement
    // .value coerces on read), but the dispatch board's options are numeric
    // database ids (driver_id, truck_id, trailer_id) that get PATCHed straight
    // back to the API as `{ driver_id: 2 }`, not `{ driver_id: '2' }`. Emitting
    // the raw string here would silently turn every selection into a string
    // and the API would receive the wrong JSON type on every save. Instead,
    // find the option whose value stringifies to the same thing the browser
    // just reported and re-emit ITS original value, number or string as
    // authored in the `options` prop. The raw string is the fallback only for
    // the case nothing matches (should not happen for a real change event,
    // since the browser can only report a value that came from an <option> in
    // this same list), so a legitimate selection is never dropped.
    handleSelectChange(event) {
      const raw = event.target.value;
      const match = (this.options || []).find(option => String(option.value) === raw);
      this.$emit('update:modelValue', match ? match.value : raw);
    },
    // Emits `submit` alongside the native Enter behaviour rather than
    // preventing it: inside a real <form> with a submit button the browser's
    // own submit must still fire. This component does not know what
    // submitting means (it has no form reference), so it stops swallowing the
    // key and lets the page decide what `submit` means (refresh_plan.md
    // 3.1.6, web paper cut 9).
    handleEnter(event) {
      this.$emit('submit', event);
    },
    // Looked up by id rather than $refs.control, because a caller-supplied
    // control (the default slot's select/masked-input case) is compiled in
    // the parent's render scope and never registers a ref on this instance.
    // Every control this component renders, default or slotted, carries
    // :id="controlId", so an attribute lookup finds either one.
    getControlEl() {
      const ref = this.$refs.control;
      // A component ref (the switch branch) is an instance, not an element;
      // its root element is what takes focus.
      if (ref) return ref.$el || ref;
      return this.$el.querySelector(`[id="${this.controlId}"]`);
    },
    focus() {
      const el = this.getControlEl();
      if (el && typeof el.focus === 'function') el.focus();
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-field {
  display: block;
}

// The label and the save indicator on one line, the indicator pushed to the
// end. `flex-wrap` so a long label on a narrow screen drops the indicator
// to its own line instead of squeezing the words.
.lw-c-field__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--lw-space-1) var(--lw-space-3);
}

.lw-c-field__label {
  display: block;
  // `font` shorthand takes the token's weight/size/line-height/family in one
  // read; tracking and case are separate roles because `font` has no shorthand
  // slot for either.
  font: var(--lw-type-label);
  letter-spacing: var(--lw-type-label-tracking);
  text-transform: var(--lw-type-label-transform);
  color: var(--lw-fg-secondary);
  margin-bottom: var(--lw-space-1);
}

.lw-c-field__required {
  color: var(--lw-danger-fg);
}

.lw-c-field__control {
  display: block;
  width: 100%;
  box-sizing: border-box;
  font: var(--lw-type-body);
  color: var(--lw-fg-primary);
  background: var(--lw-bg-paper);
  // 1px hairline: geometry with no token, per the hard rule that only a raw
  // px with no scale equivalent is allowed here.
  border: 1px solid var(--lw-line-strong);
  border-radius: var(--lw-radius-sm);
  padding: var(--lw-space-3) var(--lw-space-4);

  // The only focus treatment: the token ring via the shared mixin, nothing
  // else. This is the whole reason _forms is being dropped: Bootstrap's
  // .form-control:focus box-shadow makes tabbing from an input to a button
  // change both the ring colour and its geometry (audit_web.md 3.4).
  @include lw-focus-visible;

  &::placeholder {
    color: var(--lw-fg-tertiary);
  }

  &--error {
    border-color: var(--lw-danger-edge);
  }

  &--attention-warning {
    border-color: var(--lw-warning-edge);
    box-shadow: 0 0 0 2px var(--lw-warning-edge);
  }

  &--attention-danger {
    border-color: var(--lw-danger-edge);
    box-shadow: 0 0 0 2px var(--lw-danger-edge);
  }

  // A select keeps its platform arrow rather than getting `appearance: none`
  // and a replacement drawn in CSS: a hand-drawn arrow needs a fill colour,
  // and an SVG data URI baking in a hex value is exactly the literal
  // tokenPurity.test.js exists to catch, plus it would have to be redrawn per
  // theme. `min-inline-size` stops a short options list (a two-driver test
  // fixture, an empty board) from collapsing narrower than a value is
  // legible at, the one thing a text input never needs because typed text
  // never demands a minimum box. The end padding is headroom for the native
  // glyph so the longest option label does not run under it.
  &--select {
    min-inline-size: var(--lw-space-16);
    padding-inline-end: var(--lw-space-8);
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }

  // Everything a textarea shares with every other control — border, padding,
  // background, the error ring, the focus mixin — comes from the base rule
  // above for free, because it carries both classes. Three things are
  // specific to a multi-line control and are all this modifier adds:
  // resizing (vertical only, so a caller can grow the box without ever
  // dragging it wider than the field column), a height floor so a short
  // `rows` value never collapses thinner than Notes needs to stay usable,
  // and the body font, repeated explicitly rather than left to fall out of
  // class order: a `<textarea>`'s own UA stylesheet sets a monospace font by
  // default in some browsers, and `.lw-c-field__control` above already wins
  // against that default, but naming the token here again means this rule
  // keeps working even if that base declaration is ever reordered.
  &--textarea {
    resize: vertical;
    min-block-size: var(--lw-space-16);
    font: var(--lw-type-body);
  }

  &:disabled {
    // Colour on a fill, not opacity: opacity over a fill is exactly how the
    // driver app arrived at a 2.5:1 disabled contrast (audit_mobile.md 3.10).
    color: var(--lw-fg-disabled);
    background: var(--lw-bg-inset);
    border-color: var(--lw-line-hairline);
  }
}

// ===== Per-field save indicator =====
.lw-c-field__status {
  display: inline-flex;
  align-items: center;
  gap: var(--lw-space-1);
  margin-bottom: var(--lw-space-1);
  font: var(--lw-type-caption-strong);
  color: var(--lw-fg-secondary);

  &--saved {
    color: var(--lw-success-fg);
  }

  &--error {
    color: var(--lw-danger-fg);
  }
}

// The same ring LwButton draws while loading: em-sized so it tracks the
// caption's type, raw px for the stroke because a ring glyph has no token
// in the scale (hard rule 2's stated exception).
.lw-c-field__spinner {
  width: 0.85em;
  height: 0.85em;
  border-radius: var(--lw-radius-pill);
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: lw-c-field-spin var(--lw-motion-slow) linear infinite;
}

@keyframes lw-c-field-spin {
  to {
    transform: rotate(360deg);
  }
}

.lw-c-field__description {
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
  margin: var(--lw-space-1) 0 0;

  &--warning {
    font: var(--lw-type-caption-strong);
    color: var(--lw-warning-fg);
  }

  &--danger {
    font: var(--lw-type-caption-strong);
    color: var(--lw-danger-fg);
  }
}

.lw-c-field__error {
  font: var(--lw-type-caption-strong);
  color: var(--lw-danger-fg);
  margin: var(--lw-space-1) 0 0;
}
</style>
